import {
  birdStatusLabel,
  birdStatusTone,
  describeAge,
  shortRingNumber,
} from "@breenova/domain";
import {
  Alert,
  Button,
  Card,
  CardInset,
  CardTitle,
  ScrollRow,
  SexIcon,
  StatusPill,
  Tabs,
  cn,
} from "@breenova/ui";
import { ArrowLeft, Camera, ChevronRight, Pencil, Scale } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useBird, useDeleteBird, type BirdDetail } from "@/lib/birds";
import { WIDE_RECORD, useMediaQuery } from "@/lib/media";
import { BirdPhotoButton } from "@/routes/birds/bird-photo-button";
import { HealthTab } from "@/routes/birds/health-tab";
import { BirdExportActions } from "@/routes/settings/bird-export-actions";

type DetailTab = "overview" | "pedigree" | "rounds" | "care";

/**
 * Rounds is rendered but disabled rather than hidden — showing the shape of the record
 * is the point of the screen, and a tab that appears later moves everything the breeder
 * has already learned to reach for.
 */
const TABS = [
  { value: "overview" as const, label: "Overview" },
  { value: "pedigree" as const, label: "Pedigree" },
  { value: "rounds" as const, label: "Rounds", disabled: true },
  { value: "care" as const, label: "Care" },
];

export function BirdDetailPage({
  id: givenId,
  embedded = false,
}: {
  /** The bird to show, when the page is a pane beside a list rather than a route. */
  id?: string | undefined;
  /** Drawn in the split view's pane: no back button, and the wide layout at any width. */
  embedded?: boolean;
} = {}) {
  const params = useParams<{ id: string }>();
  const id = givenId ?? params.id;
  const navigate = useNavigate();
  const bird = useBird(id);
  const wide = useMediaQuery(WIDE_RECORD) || embedded;
  const remove = useDeleteBird();
  const [tab, setTab] = useState<DetailTab>("overview");
  const [error, setError] = useState<string | null>(null);

  if (bird.isPending) {
    return <p className="p-lg text-body text-on-surface-variant">Loading…</p>;
  }

  if (bird.isError || !bird.data) {
    return (
      <div className="flex flex-col gap-md p-lg">
        <Alert tone="error">That bird could not be found.</Alert>
        <Link to="/birds" className="text-body text-primary underline">
          Back to birds
        </Link>
      </div>
    );
  }

  const record = bird.data;
  const today = new Date().toISOString().slice(0, 10);
  const photo = record.photos.find((item) => item.isPrimary) ?? record.photos[0];
  const age = record.hatchDate ? describeAge(record.hatchDate, today) : "—";
  const speciesLine = [
    record.isHybrid && record.hybridWithSpeciesName
      ? `${record.speciesName} × ${record.hybridWithSpeciesName}`
      : record.speciesName,
    ...record.mutations.map((mutation) => mutation.name),
  ].join(" · ");

  async function handleDelete(): Promise<void> {
    setError(null);

    try {
      await remove.mutateAsync(record.id);
      await navigate("/birds", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete the bird.");
    }
  }

  /*
   * From `md`, and always in the split view's pane: the photo becomes a square beside
   * the identity rather than a banner above it. A tablet's width is better spent putting
   * the ring number next to the bird than under a 1000px strip of stripes.
   */
  if (wide) {
    return (
      <div className={cn("flex w-full flex-col gap-4.5 px-7 pb-lg pt-6.5", embedded ? "" : "md:px-gutter-wide")}>
        {embedded ? null : (
          <Link
            to="/birds"
            aria-label="Back to birds"
            className="flex size-touch items-center justify-center self-start rounded-full bg-surface-container-lowest text-on-surface shadow-card"
          >
            <ArrowLeft className="size-5" />
          </Link>
        )}

        <div className="flex items-stretch gap-5.5">
          <div className="relative size-52.5 shrink-0">
            {photo ? (
              <img src={photo.url} alt="" className="size-full rounded-photo object-cover" />
            ) : (
              <div
                aria-hidden
                className={cn(
                  "flex size-full items-center justify-center rounded-photo text-on-primary-fixed-variant",
                  "bg-[repeating-linear-gradient(135deg,var(--color-primary-fixed)_0_calc(var(--spacing)*3.5),var(--color-primary-fixed-dim)_calc(var(--spacing)*3.5)_calc(var(--spacing)*7))]",
                )}
              >
                <Camera className="size-6.5" />
              </div>
            )}

            <div className="absolute bottom-2 right-2">
              <BirdPhotoButton birdId={record.id} hasPhoto={Boolean(photo)} onError={setError} />
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="truncate text-kicker uppercase text-on-surface-variant">
                  {speciesLine}
                </span>
                <h1 className="truncate text-record-wide text-on-surface" data-numeric>
                  {record.ringNumber ?? "Unringed bird"}
                </h1>
              </div>

              <div className="flex shrink-0 gap-2">
                <Link
                  to={`/birds/${record.id}/edit`}
                  className={cn(
                    "flex h-touch items-center gap-1.5 rounded-control px-lg",
                    "bg-surface-container-lowest text-pill text-on-surface shadow-card",
                    "transition-colors hover:bg-surface-container-low",
                  )}
                >
                  <Pencil className="size-4" aria-hidden />
                  Edit
                </Link>
                {/* Weighing is logged on the Care tab, so the button goes there. */}
                <button
                  type="button"
                  onClick={() => setTab("care")}
                  className={cn(
                    "flex h-touch items-center gap-1.5 rounded-control px-lg",
                    "bg-action text-cta-xs text-on-action transition-opacity hover:opacity-90",
                  )}
                >
                  <Scale className="size-4" aria-hidden />
                  Weigh
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <StatusPill tone={birdStatusTone(record.status)}>
                {birdStatusLabel(record.status)}
              </StatusPill>
              {record.isHybrid ? <StatusPill tone="neutral">Mule</StatusPill> : null}
            </div>

            <dl
              className="grid grid-cols-4 gap-2 rounded-card bg-surface-container px-4.5 py-3.5"
              data-numeric
            >
              <Fact label="Sex" value={sexLabel(record.sex)} size="large" />
              <Fact label="Age" value={age} size="large" />
              <Fact label="Cage" value={record.locationCode ?? "—"} size="large" />
              <Fact label="Hatched" value={record.hatchDate ? longDate(record.hatchDate) : "—"} size="large" />
            </dl>
          </div>
        </div>

        <Tabs
          label="Bird sections"
          value={tab}
          items={TABS}
          onChange={(next) =>
            next === "pedigree" ? void navigate(`/birds/${record.id}/pedigree`) : setTab(next)
          }
        />

        {error ? <Alert tone="error">{error}</Alert> : null}

        {tab === "overview" ? (
          <Overview record={record} onDelete={handleDelete} deleting={remove.isPending} wide />
        ) : null}

        {tab === "care" ? <HealthTab birdId={record.id} bare /> : null}
      </div>
    );
  }

  return (
    /*
     * Capped and centred from `md`.
     *
     * A bird's record is one animal's page, not a dashboard: the photo, the identity card
     * and the facts under it are a single column of content that does not become more
     * informative for being 1200px wide — it becomes a 300px-tall photograph stretched to
     * a letterbox, with a ring number floating alone in the middle of it. Capping is what
     * makes this read as a desktop record page rather than a phone screen pulled sideways.
     */
    /*
     * Capped at a form's width up to `lg`, and uncapped above it.
     *
     * The cap is right while the record is a stack: a column of fact cards 1100px wide is
     * six words per line and a lot of white to the right of them. It stops being right
     * once the facts lay out in three columns, which is the width the cap was preventing.
     */
    <div className="flex w-full flex-col md:mx-auto md:max-w-wide-form xl:max-w-none">
      {/*
       * The photo runs full-bleed to the top of the screen and rounds off at the
       * bottom, so the identity card below can climb into it. Nothing else on the
       * screen is edge to edge.
       */}
      <header className="relative">
        {photo ? (
          <img
            src={photo.url}
            alt=""
            // Shorter on a desktop: 300px of photo is a third of a phone screen and a
            // fifth of a laptop's, and at the same height it stops being a portrait and
            // starts being a banner.
            className="h-72.5 w-full rounded-b-hero object-cover md:h-56 xl:h-64"
          />
        ) : (
          <div
            aria-hidden
            className={cn(
              "flex h-72.5 w-full items-center justify-center rounded-b-hero md:h-56 xl:h-64",
              // Diagonal stripes rather than a flat block: a solid panel where a photo
              // should be reads as an image that failed to load.
              "bg-[repeating-linear-gradient(135deg,var(--color-primary-fixed)_0_calc(var(--spacing)*3.5),var(--color-primary-fixed-dim)_calc(var(--spacing)*3.5)_calc(var(--spacing)*7))]",
            )}
          >
            <span className="-mt-10 flex flex-col items-center gap-1.5 text-subtitle text-on-primary-fixed-variant">
              <Camera className="size-6.5" />
              Bird photo
            </span>
          </div>
        )}

        <div className="absolute inset-x-0 top-0 flex items-center justify-between px-5 pt-1.5 md:px-gutter-wide md:pt-xl">
          <Link
            to="/birds"
            aria-label="Back to birds"
            className="flex size-touch items-center justify-center rounded-full bg-surface-container-lowest/90 text-on-surface"
          >
            <ArrowLeft className="size-5" />
          </Link>

          <div className="flex items-center gap-sm">
            <BirdPhotoButton birdId={record.id} hasPhoto={Boolean(photo)} onError={setError} />

            <Link
              to={`/birds/${record.id}/edit`}
              aria-label="Edit this bird"
              className="flex size-touch items-center justify-center rounded-full bg-surface-container-lowest/90 text-on-surface"
            >
              <Pencil className="size-4.5" />
            </Link>
          </div>
        </div>
      </header>

      {/*
        The column climbs 70px into the photo. The card is the first thing in it, so the
        overlap belongs to the column rather than to the card itself.

        `relative z-10` is what makes the climb visible. The header above is positioned —
        it has to be, for the back and edit buttons — so it paints over any static sibling
        that follows it, and the 70px the card climbs was the 70px holding the ring
        number. A negative margin moved the card; it took a stacking context to show it.
      */}
      <div className="relative z-10 -mt-17.5 flex flex-col gap-4 px-5 md:px-gutter-wide">
        <Card className="flex flex-col gap-3.5 p-lg shadow-raised">
          <div className="flex items-start justify-between gap-md">
            <div className="flex min-w-0 flex-col gap-0.75">
              <span className="truncate text-kicker uppercase text-on-surface-variant">
                {record.speciesName}
                {record.isHybrid && record.hybridWithSpeciesName
                  ? ` × ${record.hybridWithSpeciesName}`
                  : ""}
                {record.mutations.length > 0
                  ? ` · ${record.mutations.map((mutation) => mutation.name).join(", ")}`
                  : ""}
              </span>
              <h1 className="truncate text-record text-on-surface" data-numeric>
                {record.ringNumber ?? "Unringed bird"}
              </h1>
            </div>

            <StatusPill tone={birdStatusTone(record.status)}>
              {birdStatusLabel(record.status)}
            </StatusPill>
          </div>

          <CardInset className="px-3.5 py-3">
            <dl className="grid grid-cols-3 gap-1.5" data-numeric>
              <Fact label="Sex" value={sexLabel(record.sex)} />
              <Fact
                label="Age"
                value={record.hatchDate ? describeAge(record.hatchDate, today) : "—"}
              />
              <Fact label="Cage" value={record.locationCode ?? "—"} />
            </dl>
          </CardInset>
        </Card>

        <Tabs
          label="Bird sections"
          value={tab}
          items={TABS}
          onChange={(next) =>
            next === "pedigree" ? void navigate(`/birds/${record.id}/pedigree`) : setTab(next)
          }
        />

        {error ? <Alert tone="error">{error}</Alert> : null}
      </div>

      {tab === "overview" ? (
        <Overview record={record} onDelete={handleDelete} deleting={remove.isPending} />
      ) : null}

      {tab === "care" ? <HealthTab birdId={record.id} /> : null}
    </div>
  );
}

function Overview({
  record,
  onDelete,
  deleting,
  wide = false,
}: {
  record: BirdDetail;
  onDelete: () => void;
  deleting: boolean;
  /** Two cards to a row, under the wide record's photo and identity. */
  wide?: boolean;
}) {
  return (
    /* Each card here is a short fixed block — parents, identification, mutations — and
       stacking six of them makes a wide reader scroll past facts that would have fitted
       on one screen. */
    <div
      className={cn(
        wide
          ? "grid grid-cols-2 items-start gap-3.5"
          : "flex flex-col gap-4 px-5 pt-4",
      )}
    >
      <Card className={cn("flex flex-col gap-3", wide ? "p-lg" : "p-4")}>
        <CardTitle className={wide ? "text-pair-title" : undefined}>Parents</CardTitle>

        <div className={cn("grid gap-2", wide ? "grid-cols-1" : "grid-cols-2")}>
          <ParentChip
            label="Sire"
            sex="Male"
            id={record.sireId}
            ringNumber={record.sireRingNumber}
            row={wide}
          />
          <ParentChip
            label="Dam"
            sex="Female"
            id={record.damId}
            ringNumber={record.damRingNumber}
            row={wide}
          />
        </div>
      </Card>

      <Card className={cn("flex flex-col gap-3.5", wide ? "p-lg" : "p-4")}>
        <CardTitle className={wide ? "text-pair-title" : undefined}>Identification</CardTitle>

        <dl className="grid grid-cols-2 gap-3.5" data-numeric>
          <Fact weight="plain" label="Ring size" value={record.ringSizeName ?? "—"} />
          <Fact weight="plain" label="Ring year" value={record.ringYear?.toString() ?? "—"} />
          <Fact weight="plain" label="Federation" value={record.federationName ?? "—"} />
          <Fact weight="plain" label="Origin" value={formatOrigin(record.origin)} />
          <Fact weight="plain" label="Hatched" value={record.hatchDate ?? "—"} />
          <Fact weight="plain" label="Location" value={record.locationPath ?? "Not in the aviary"} />
        </dl>
      </Card>

      {record.mutations.length > 0 ? (
        <Card className="flex flex-col gap-3 p-4">
          <CardTitle>Mutations</CardTitle>
          <ScrollRow aria-label="Mutations" className="gap-sm">
            {record.mutations.map((mutation) => (
              <li key={mutation.mutationId} className="shrink-0">
                <StatusPill
                  // A split is carried, not shown — the pill has to make that visible
                  // or a breeder reads the bird's phenotype wrong at a glance.
                  tone={mutation.isVisible ? "biological" : "neutral"}
                >
                  {mutation.name}
                  {mutation.isVisible ? "" : " (split)"}
                </StatusPill>
              </li>
            ))}
          </ScrollRow>
        </Card>
      ) : null}

      {record.recentMovements.length > 0 ? (
        <Card className={cn("flex flex-col gap-2.5", wide ? "col-span-2 p-lg" : "p-4")}>
          <CardTitle className={wide ? "text-pair-title" : undefined}>Recent</CardTitle>
          <ul className="flex flex-col gap-2.5">
            {record.recentMovements.map((movement) => (
              <li key={movement.id} className="flex items-center gap-3 text-cell font-normal text-on-surface">
                <span aria-hidden className="size-2.25 shrink-0 rounded-full bg-primary" />
                <span className="w-16 shrink-0 font-bold text-on-surface-variant" data-numeric>
                  {shortDate(movement.movedOn)}
                </span>
                <span className="min-w-0 flex-1" data-numeric>
                  Moved {movement.fromLocationCode ? `from ${movement.fromLocationCode} ` : ""}
                  to {movement.toLocationCode ?? "outside the aviary"}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {record.acquiredFrom || record.acquiredOn ? (
        <Card className="flex flex-col gap-3 p-4">
          <CardTitle>Acquisition</CardTitle>
          <dl className="grid grid-cols-2 gap-md" data-numeric>
            <Fact weight="plain" label="Acquired" value={record.acquiredOn ?? "—"} />
            <Fact weight="plain" label="From" value={record.acquiredFrom ?? "—"} />
            {record.acquiredPrice !== null && record.acquiredPrice !== undefined ? (
              <Fact
                weight="plain"
                label="Price"
                value={`${record.acquiredPrice} ${record.acquiredCurrency ?? ""}`.trim()}
              />
            ) : null}
          </dl>
        </Card>
      ) : null}

      {record.notes ? (
        <Card className="flex flex-col gap-3 p-4">
          <CardTitle>Notes</CardTitle>
          <p className="whitespace-pre-wrap text-body text-on-surface">{record.notes}</p>
        </Card>
      ) : null}

      <div className={wide ? "col-span-2" : undefined}>
        <BirdExportActions birdId={record.id} ringNumber={record.ringNumber} />
      </div>

      <Button
        variant="danger"
        onClick={onDelete}
        disabled={deleting}
        className={cn("self-start", wide ? "col-span-2 justify-self-start" : "")}
      >
        {deleting ? "Deleting…" : "Delete bird"}
      </Button>
    </div>
  );
}

function Fact({
  label,
  value,
  weight = "bold",
  size = "default",
}: {
  label: string;
  value: string;
  /** Bold in the identity strip, where three facts are read at a glance; plain in a card. */
  weight?: "bold" | "plain";
  /** `large` in the wide record's four-fact strip, which has the room. */
  size?: "default" | "large";
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-fact-label uppercase text-on-surface-variant">{label}</dt>
      <dd
        className={cn(
          "truncate text-on-surface",
          weight === "plain" ? "text-body-tight" : size === "large" ? "text-row" : "text-label",
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/** A parent as a chip: a sexed disc, the short ring number, and where it leads. */
function ParentChip({
  label,
  sex,
  id,
  ringNumber,
  row = false,
}: {
  label: string;
  sex: "Male" | "Female";
  id: string | null | undefined;
  ringNumber: string | null | undefined;
  /** A full-width row with a chevron, for the wide record's stacked parents. */
  row?: boolean;
}) {
  const body = (
    <>
      <span
        aria-hidden
        className={cn(
          "flex size-8.5 shrink-0 items-center justify-center rounded-full",
          sex === "Male"
            ? "bg-primary-fixed-dim text-on-primary-fixed"
            : "bg-secondary-fixed-dim text-on-secondary-fixed",
        )}
      >
        <SexIcon sex={sex} className="size-3.75" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-data text-on-surface" data-numeric>
          {ringNumber ? shortRingNumber(ringNumber) : "Unknown"}
        </span>
        <span className="text-note text-on-surface-variant">{label}</span>
      </span>

      {row && id ? (
        <ChevronRight className="size-4.5 shrink-0 text-outline" aria-hidden />
      ) : null}
    </>
  );

  const className = "flex items-center gap-2.5 rounded-inset bg-surface-container p-2.5";

  return id ? (
    <Link to={`/birds/${id}`} className={cn(className, "hover:bg-surface-container-high")}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

function formatOrigin(origin: string): string {
  // The enum names are readable already except for the compound one.
  return origin === "BredHere" ? "Bred here" : origin;
}

/** "Cock ♂", the way a breeder writes it on a cage card. */
function sexLabel(sex: BirdDetail["sex"]): string {
  return sex === "Male" ? "Cock ♂" : sex === "Female" ? "Hen ♀" : "Unknown";
}

/** "28 Sep". Stored UTC, read in the breeder's own calendar. */
function shortDate(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** "12 May 2025". */
function longDate(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
